
import Vue from 'vue';
import { Component } from 'vue-property-decorator';
import UserGroup from '@/models/UserGroup';
import UserGroupRepository from '@/repositories/UserGroupRepository';
import UserGroupAPI from '@/api/UserGroupAPI';
import { downloadTableAsExcel, ExportColumn } from '@/helpers/PrintHelper';

@Component({ name: 'CmsB2BGroupsOverviewTable' })
export default class CmsB2BGroupsOverviewTable extends Vue {
    private search: string = '';
    private isLoading: boolean = false;
    private archivingId: string | null = null;

    private get userGroups() {
        return UserGroupRepository.getAll();
    }

    private get columns() {
        return [
            {
                title: this.$t('ID'),
                dataIndex: 'id',
                sorter: (a: any, b: any) => Number(a.id) - Number(b.id),
                sortDirections: ['descend', 'ascend'],
                defaultSortOrder: 'ascend',
                width: '8%',
            },
            {
                title: this.$t('Name'),
                dataIndex: 'name',
                sorter: (a: any, b: any) => a.name.localeCompare(b.name),
                sortDirections: ['descend', 'ascend'],
                width: '25%',
            },
            {
                title: this.$t('Client'),
                dataIndex: 'clientName',
                sorter: (a: any, b: any) => (a.clientName || '').localeCompare(b.clientName || ''),
                sortDirections: ['descend', 'ascend'],
                width: '25%',
            },
            {
                title: this.$t('Country'),
                dataIndex: 'countryCode',
                scopedSlots: { customRender: 'countrySlot' },
                sorter: (a: any, b: any) => (a.countryCode || '').localeCompare(b.countryCode || ''),
                sortDirections: ['descend', 'ascend'],
                width: '12%',
                align: 'center',
            },
            {
                title: this.$t('Archived'),
                dataIndex: 'isArchived',
                scopedSlots: { customRender: 'archivedSlot' },
                sorter: (a: any, b: any) => Number(a.isArchived) - Number(b.isArchived),
                sortDirections: ['descend', 'ascend'],
                width: '12%',
                align: 'center',
            },
            {
                title: this.$t('Actions'),
                key: 'actions',
                dataIndex: 'id',
                scopedSlots: { customRender: 'actionsSlot' },
                width: '10%',
                align: 'center',
            },
        ];
    }

    private get filteredList(): any[] {
        if (!this.userGroups || !this.userGroups.length) {
            return [];
        }
        return this.userGroups
            .filter(
                (group: any) =>
                    group.name.toLowerCase().includes(this.search.toLowerCase()) ||
                    (group.clientName || '').toLowerCase().includes(this.search.toLowerCase()),
            )
            .map((group: any) => ({
                id: group.id,
                key: group.id,
                name: group.name,
                clientName: group.clientName || '—',
                countryCode: group.countryCode || '—',
                isArchived: group.isArchived || false,
            }));
    }

    private get total(): number {
        return this.filteredList.length;
    }

    private async toggleArchive(record: any) {
        this.archivingId = record.id;
        try {
            await UserGroupAPI.archive(record.id);
            await UserGroup.getAll();
            this.$notification.success({
                message: record.isArchived
                    ? (this.$t('Group unarchived successfully') as string)
                    : (this.$t('Group archived successfully') as string),
                description: '',
            });
        } catch (e) {
            this.$notification.error({
                message: this.$t('Error updating group') as string,
                description: (e as Error).message,
            });
        } finally {
            this.archivingId = null;
        }
    }

    private get exportList(): any[] {
        return this.filteredList.map((row: any) => ({
            ...row,
            isArchived: row.isArchived ? this.$t('Yes') : this.$t('No'),
        }));
    }

    private downloadExcel() {
        const tableData = this.exportList;
        if (!tableData || tableData.length === 0) {
            return;
        }
        const exportColumns = [
            { title: this.$t('ID'), dataIndex: 'id' },
            { title: this.$t('Name'), dataIndex: 'name' },
            { title: this.$t('Client'), dataIndex: 'clientName' },
            { title: this.$t('Country'), dataIndex: 'countryCode' },
            { title: this.$t('Archived'), dataIndex: 'isArchived' },
        ] as ExportColumn[];

        downloadTableAsExcel(exportColumns, tableData, 'B2B_Groups_Overview');
    }

    private async mounted() {
        this.isLoading = true;
        try {
            await UserGroup.getAll();
        } catch (e) {
            this.$notification.error({
                message: this.$t('Error loading groups') as string,
                description: (e as Error).message,
            });
        } finally {
            this.isLoading = false;
        }
    }
}
